/* eslint-disable consistent-return */

/**
 * @file LT 信息披露页通用数据模块
 * @date 2021-12-09
 */

import { replace } from '@/locales/i18n';
import { swapapi } from '@/apis/api2';
import { create as createBackoff, remove as removeBackoff, get as getBackoff } from '@/utils/backoff';
import { ltApi } from '@/apis/spot';

const state = {
    /**
     * 当前页面的交易对 ID
     *
     * @type {string}
     */
    symbolId: '',

    /**
      * 当前交易对的最新行情数据
      *
      * @type {Object}
      */
    klineinfo: {},

    // 合约币对 map 信息
    swapSymbols: {},

    // 申购赎回弹窗
    isShowltModal: false,
    // 申购赎回ws
    ltExhangeData: null,

    // 教学视频信息
    learningVideo: null,
};

const getters = {
    swapSymbols(state) {
        return state.swapSymbols || {};
    },
    symbols(state, _, __, rootGetters) {
        const symbols = rootGetters['settings/symbols'];
        return symbols.filter((item) => item.symbolType === 'LT' && !item.noticeVisible);
    },
};

const mutations = {
    SET_SYMBOL_ID(state, payload) {
        state.symbolId = payload;
    },
    SET_KLINEINFO(state, payload) {
        state.klineinfo = payload || {};
    },
    SET_SWAP_SYMBOLS(state, payload) {
        state.swapSymbols = payload;
    },
    CHANGE_LI_BUY_SELL_MODAL_STATUS(state, payload) {
        state.isShowltModal = payload;
    },
    SET_LT_EXCHANGE_DATA(state, payload) {
        state.ltExhangeData = payload;
    },

    SET_LEARNING_VIDEO(state, payload) {
        state.learningVideo = payload;
    },
};

const actions = {

    // 判断etp申购赎回弹窗
    SET_LT_BUY_SELL_MODAL_STATUS({ commit, rootState, dispatch }) {
        const { exchange: { ltRisk: { isPassTest, isConfirmOpen } } } = rootState;
        if (!isConfirmOpen) {
            commit('exchange/UPDATE_LT_LAYER', true, {
                root: true,
            });
            return;
        }
        if (!isPassTest && isConfirmOpen) {
            dispatch('exchange/GET_LT_INFO', null, {
                root: true,
            });
            return null;
        }
        commit('CHANGE_LI_BUY_SELL_MODAL_STATUS', true);
    },
    // 设置币对ID
    SET_SYMBOL_ID({
        commit, state, dispatch, getters,
    }, { baseTokenId, quoteTokenId } = {}) {
        const { symbols } = getters;
        if (!symbols.length) return;

        // 优先使用链接中的交易对
        let symbol = baseTokenId && quoteTokenId
            ? symbols.find((item) => (
                item.baseTokenId === baseTokenId.toUpperCase() && item.quoteTokenId === quoteTokenId.toUpperCase()
            ))
            : null;

        // 如果没有交易对，则显示默认杠杆代币币对列表第一个
        if (!symbol) {
            [symbol] = symbols;
        }

        if (
            (baseTokenId && quoteTokenId && baseTokenId !== symbol.baseTokenId && quoteTokenId !== symbol.quoteTokenId)
            || !state.symbolId
            || state.symbolId !== symbol.symbolId
        ) {
            if (!window.sfe.prerender) {
                const { origin, search, hash } = window.location;
                const langUrl = replace(`/trade/spot/lt/${symbol.baseTokenId}/${symbol.quoteTokenId}`);
                const url = `${origin}${langUrl}${search}${hash}`;

                window.history.replaceState(null, '', url);
            }
        }

        commit('SET_SYMBOL_ID', symbol.symbolId);
    },

    // 设置合约币对信息
    async GET_SWAP_SYMBOLS({ commit, dispatch }) {
        const res = await swapapi.getSwapSymbols();
        if (res.success && res.result) {
            const swapSymbols = {};
            Object.entries(res.result).forEach(([, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((symbol) => {
                        swapSymbols[symbol.symbolName] = symbol;
                    });
                }
            });

            commit('SET_SWAP_SYMBOLS', swapSymbols);
            removeBackoff('GET_SWAP_SYMBOLS');
        } else if (!getBackoff('GET_SWAP_SYMBOLS')) {
            createBackoff('GET_SWAP_SYMBOLS', {
                min: 1000,
                max: 10000,
                maxCount: 3,
                callback() {
                    return dispatch('GET_SWAP_SYMBOLS');
                },
            });
        }
    },

    async GET_LEARNING_VIDEO({ commit }) {
        const res = await ltApi.getLtLearningVideo();
        if (res.success && res.result) {
            commit('SET_LEARNING_VIDEO', res.result);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
